/*
 * 电子推荐卡管理列表的演示数据。
 * 14 条按业务场景命名，覆盖「进行中 / 未开始 / 已结束 / 已停用」四种状态与两种分享范围，
 * 用于核对列内容与操作集。全部为本地模拟数据，不连接真实接口。
 *
 * 日期写成相对今天的天数（见 `CardSeed`），活动窗口因此永远跟着今天走，不会整批过期。
 */

import { demoToday, shiftDays } from '../../../../lib/demoTime';

export type RecommendationCardTimeStatus = '进行中' | '未开始' | '已结束';

/** 「已停用」是人工操作的结果，不由时间推出，因此独立于时间派生状态之外。 */
export type RecommendationCardStatus = RecommendationCardTimeStatus | '已停用';

export type RecommendationShareScope = '指定学员' | '所有学员';

export interface RecommendationCard {
  /** 已授权教练数；0 表示未授权，列表显示「未授权」。 */
  authorizedCoaches: number;
  /** 人为停用的活动卡。与时间无关，由列表里的「启用 / 停用」开关写入。 */
  closed?: boolean;
  endTime: string;
  id: number;
  name: string;
  shareScope: RecommendationShareScope;
  startTime: string;
}

/** 种子用相对天数表达活动窗口；`buildRecommendationCards` 再落到具体日期。 */
export type CardSeed = Omit<RecommendationCard, 'endTime' | 'startTime'> & {
  readonly endDay: number;
  readonly startDay: number;
};

export function buildRecommendationCards(seeds: readonly CardSeed[], today: string = demoToday): readonly RecommendationCard[] {
  return seeds.map(({ endDay, startDay, ...rest }) => ({
    ...rest,
    endTime: shiftDays(today, endDay),
    startTime: shiftDays(today, startDay),
  }));
}

/** 状态是派生值：先读人工关闭标志，再按活动窗口与时间的关系推导。 */
export function deriveCardStatus(card: RecommendationCard, today: string = demoToday): RecommendationCardStatus {
  if (card.closed) return '已停用';
  if (card.startTime > today) return '未开始';
  if (card.endTime < today) return '已结束';
  return '进行中';
}

/*
 * 每条按其想展示的状态摆窗口：
 *   进行中 — 窗口跨越今天
 *   未开始 — 窗口整体在未来
 *   已结束 — 窗口整体落在过去
 *   已停用 — 窗口仍在有效期内，但被人工停掉（停用后仍能「启用」）
 */
export const cardSeeds: readonly CardSeed[] = [
  { authorizedCoaches: 0, endDay: 289, id: 1, name: '阶梯电子推荐卡', shareScope: '指定学员', startDay: -111 },
  { authorizedCoaches: 0, endDay: -79, id: 2, name: '小升初手拉手活动', shareScope: '指定学员', startDay: -111 },
  { authorizedCoaches: 3, endDay: 133, id: 3, name: '秋季老带新转介绍卡', shareScope: '所有学员', startDay: -19 },
  { authorizedCoaches: 1, endDay: -20, id: 4, name: '暑期续班推荐卡', shareScope: '指定学员', startDay: -67 },
  { authorizedCoaches: 0, endDay: 161, id: 5, name: '寒假集训营推荐卡', shareScope: '指定学员', startDay: 91 },
  { authorizedCoaches: 0, endDay: 102, id: 6, name: '新生体验课推荐卡', shareScope: '所有学员', startDay: -41 },
  { authorizedCoaches: 2, endDay: -31, id: 7, name: '老生续费推荐卡', shareScope: '指定学员', startDay: -123 },
  { authorizedCoaches: 0, endDay: 61, id: 8, name: '双十一拼团推荐卡', shareScope: '所有学员', startDay: 30 },
  { authorizedCoaches: 0, closed: true, endDay: 117, id: 9, name: '元旦限时推荐卡', shareScope: '指定学员', startDay: -30 },
  { authorizedCoaches: 5, endDay: 162, id: 10, name: '家长转介绍奖励卡', shareScope: '所有学员', startDay: -203 },
  { authorizedCoaches: 0, endDay: -72, id: 11, name: '春季开学推荐卡', shareScope: '指定学员', startDay: -213 },
  { authorizedCoaches: 1, endDay: -26, id: 12, name: '暑期体验营推荐卡', shareScope: '所有学员', startDay: -92 },
  { authorizedCoaches: 0, closed: true, endDay: 90, id: 13, name: '期末冲刺推荐卡', shareScope: '指定学员', startDay: -60 },
  { authorizedCoaches: 2, endDay: 355, id: 14, name: '周年庆老带新推荐卡', shareScope: '所有学员', startDay: -10 },
];

export const recommendationCards: readonly RecommendationCard[] = buildRecommendationCards(cardSeeds);

export const statusFilterOptions = [
  { label: '不限', value: '' },
  { label: '进行中', value: '进行中' },
  { label: '未开始', value: '未开始' },
  { label: '已结束', value: '已结束' },
  { label: '已停用', value: '已停用' },
] as const;
